/**
* @Author: Michael Neumair <mBook>
* @Date:   2016-08-22T13:39:18+02:00
* @Email:  7q7w7e7r@gmail.com
* @Last modified by:   mBook
* @Last modified time: 2016-09-21T16:30:35+02:00
*/

// based on: https://github.com/callemall/material-ui/blob/master/src/Dialog/Dialog.js

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import EventListener from 'react-event-listener';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import keycode from 'keycode';
import transitions from './../../../transitions/transitions';
import Overlay from 'common/overlay/components/Overlay';
import MPaperMain from 'common/papers/components/MPaperMain';
import AHeadlineDialog from './AHeadlineDialog';
import autoPrefix from './../../../utilities/autoPrefix';
import ScaleIn from 'transitions/ScaleIn';


function getSpacing() {
  const clientHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  return (clientHeight < 640) ? 10 : 64;
}

class MDialogInline extends Component { // eslint-disable-line react/no-multi-comp
  componentDidMount() {
    this.positionDialog();
  }

  componentDidUpdate() {
    this.positionDialog();
  }

  render() {
    const {
      actions,
      autoScrollBodyContent,
      children,
      open,
      title,
    } = this.props;

    const scrollBodyContentClass = autoScrollBodyContent ? 'scroll-body-content' : '';

    const styles = {
      root: {
        left: open ? 0 : -10000,
        transition: open ?
                    transitions.easeOut('0ms', 'left', '0ms') :
                    transitions.easeOut('0ms', 'left', '450ms'),
      },
    };

    // actions-continer
    let actionsContainer;
    if (React.Children.count(actions) > 0) {
      actionsContainer = (
        <div className="m-dialog-inline__action-container">
          {React.Children.toArray(actions)}
        </div>
      );
    }

    // title
    let titleElement;
    if (React.isValidElement(title)) {
      titleElement = (
        <div className="m-dialog-inline__title">
          {title}
        </div>
      );
    } else if (typeof title === 'string') {
      titleElement = (
        <div className="m-dialog-inline__title">
          <AHeadlineDialog>
            {title}
          </AHeadlineDialog>
        </div>
      );
    }

    return (
      <div
        ref={this.refContainer}
        className={`m-dialog m-dialog-inline ${scrollBodyContentClass}`}
        style={styles.root}
      >
        <EventListener
          target="window"
          onKeyUp={this.handleKeyUp}
          onResize={this.handleResize}
        />
        <ScaleIn
          key="scalein"
          className="m-dialog-inline__box"
          childStyle={{
                    	position: 'relative',
                    	transition: 'all 360ms',
          }}
        >
          { open &&
                        <MPaperMain
                          style={{}}
                          key={'open-date-dialog'}
                        >
                          {titleElement}
                          <div
                            className="m-dialog-inline__body"
                          >
                            {children}
                          </div>
                          {actionsContainer}
                        </MPaperMain> ||
                        <div key="close-date-dialog"/>
          }
        </ScaleIn>
        <Overlay
          show={open}
          onClick={this.onClickOverlay}
        />
      </div>
    );
  }

    refContainer = (node) => {
      this._container = node;
    }

    onClickOverlay = () => {
      this.requestClose(false);
    };

    handleKeyUp = (event) => {
      if (keycode(event) === 'esc') {
        this.requestClose(false);
      }
    };

    handleResize = () => {
      this.positionDialog();
    };

    positionDialog() {
      const {
        actions,
        autoDetectWindowHeight,
        autoScrollBodyContent,
        open,
        repositionOnUpdate,
        title,
      } = this.props;

      const clientHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      const container = this._container;
      const dialogWindow = this._container.getElementsByClassName('m-paper-main')[0];
      const dialogBody = this._container.getElementsByClassName('m-dialog-inline__body')[0];
      const minPaddingTop = 10;
      const spacing = getSpacing();

      if (!dialogWindow || !dialogBody) {
        return;
      }

      // Reset the height in case the window was resized.
      dialogWindow.style.height = '';
      dialogBody.style.height = '';

      const dialogWindowHeight = dialogWindow.offsetHeight;
      let paddingTop = ((clientHeight - dialogWindowHeight) / 2) - spacing;
      if (paddingTop < minPaddingTop) paddingTop = minPaddingTop;

      // Vertically center the dialog window, but make sure it doesn't
      // transition to that position.
      if (repositionOnUpdate || !container.style.paddingTop) {
        container.style.paddingTop = `${paddingTop}px`;
      }

      // Force a height if the dialog is taller than clientHeight
      if (autoDetectWindowHeight || autoScrollBodyContent) {
        let maxDialogContentHeight = clientHeight - 2 * spacing;

        if (title) maxDialogContentHeight -= dialogBody.previousSibling.offsetHeight;

        if (React.Children.count(actions)) {
          maxDialogContentHeight -= dialogBody.nextSibling.offsetHeight;
        }

        dialogBody.style.maxHeight = `${maxDialogContentHeight}px`;
      }
    }

    requestClose(buttonClicked) {
      if (!buttonClicked && this.props.modal) {
        return;
      }

      if (this.props.onRequestClose) {
        this.props.onRequestClose(!!buttonClicked);
      }
    }
}
MDialogInline.propTypes = {
  /**
    * Action buttons to display below the Dialog content (`children`).
    * This property accepts either a React element, or an array of React elements.
    */
  actions: PropTypes.node,
  /**
    * If set to true, the height of the `Dialog` will be auto detected. A max height
    * will be enforced so that the content does not extend beyond the viewport.
    */
  autoDetectWindowHeight: PropTypes.bool,
  /**
    * If set to true, the body content of the `Dialog` will be scrollable.
    */
  autoScrollBodyContent: PropTypes.bool,
  /**
    * The contents of the `Dialog`.
    */
  children: PropTypes.node,
  /**
    * Force the user to use one of the actions in the `Dialog`.
    * Clicking outside the `Dialog` will not trigger the `onRequestClose`.
    */
  modal: PropTypes.bool,
  /**
    * Fired when the `Dialog` is requested to be closed by a click outside the `Dialog` or on the buttons.
    *
    * @param {bool} buttonClicked Determines whether a button click triggered this request.
    */
  onRequestClose: PropTypes.func,
  /**
    * Controls whether the Dialog is opened or not.
    */
  open: PropTypes.bool.isRequired,
  /**
    * Determines whether the `Dialog` should be repositioned when it's contents are updated.
    */
  repositionOnUpdate: PropTypes.bool,
  /**
    * The title to display on the `Dialog`. Could be number, string, element or an array containing these types.
    */
  title: PropTypes.node,
};
MDialogInline.defaultProps = {
  autoDetectWindowHeight: true,
  autoScrollBodyContent: false,
  modal: false,
  repositionOnUpdate: true,
};
export default MDialogInline;

